import type {
  JobRow,
  PersonDetails,
  PersonInput,
  UpcomingDatesResponse,
  CaptureLogResponse,
} from "./types";

export interface AuthUser {
  id: number;
  login: string;
  first_name: string;
  last_name: string;
  email: string | null;
  title: string | null;
  bio: string | null;
  role: "admin" | "user";
  is_active: number;
  created_at: string;
  updated_at: string;
}

export async function getMe(): Promise<AuthUser | null> {
  const res = await fetch("/api/auth/me");
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`getMe failed: ${res.status}`);
  const data = (await res.json()) as { user: AuthUser };
  return data.user;
}

export async function login(username: string, password: string): Promise<AuthUser> {
  const res = await fetch("/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  if (res.status === 401) throw new Error("Invalid username or password");
  if (!res.ok) throw new Error(`login failed: ${res.status}`);
  const data = (await res.json()) as { user: AuthUser };
  return data.user;
}

// — User list (dropdown) ----------------------------------------------------

export interface UserSummary {
  id: number;
  login: string;
  first_name: string;
  last_name: string;
}

export async function listActiveUsersForDropdown(): Promise<UserSummary[]> {
  const res = await fetch("/api/users");
  if (!res.ok) throw new Error(`listActiveUsersForDropdown failed: ${res.status}`);
  const data = (await res.json()) as { users: UserSummary[] };
  return data.users;
}

// — Admin user CRUD --------------------------------------------------------

export async function adminListUsers(): Promise<AuthUser[]> {
  const res = await fetch("/api/users/admin");
  if (!res.ok) throw new Error(`adminListUsers failed: ${res.status}`);
  const data = (await res.json()) as { users: AuthUser[] };
  return data.users;
}

export interface AdminCreateUserInput {
  login: string;
  password: string;
  first_name: string;
  last_name?: string;
  email?: string | null;
  title?: string | null;
  bio?: string | null;
  role?: "admin" | "user";
}

export async function adminCreateUser(input: AdminCreateUserInput): Promise<AuthUser> {
  const res = await fetch("/api/users/admin", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `adminCreateUser failed: ${res.status}`);
  }
  const data = (await res.json()) as { user: AuthUser };
  return data.user;
}

export interface AdminPatchUserInput {
  first_name?: string;
  last_name?: string;
  email?: string | null;
  title?: string | null;
  bio?: string | null;
  role?: "admin" | "user";
  is_active?: boolean;
  password?: string;
}

export async function adminPatchUser(id: number, patch: AdminPatchUserInput): Promise<AuthUser> {
  const res = await fetch(`/api/users/admin/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `adminPatchUser failed: ${res.status}`);
  }
  const data = (await res.json()) as { user: AuthUser };
  return data.user;
}

export async function adminDeleteUser(id: number): Promise<void> {
  const res = await fetch(`/api/users/admin/${id}`, { method: "DELETE" });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `adminDeleteUser failed: ${res.status}`);
  }
}

export async function logout(): Promise<void> {
  await fetch("/api/auth/logout", { method: "POST" });
}

export interface ProfilePatchInput {
  // Identity
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  middleName?: string | null;
  suffix?: string | null;
  // Identifiers
  licenseNumber?: string | null;
  ssn?: string | null;
  zipCode?: string | null;
  // Address & contact (petitioner_details JSON)
  streetAddress?: string | null;
  city?: string | null;
  state?: string | null;
  phone?: string | null;
  email?: string | null;
  sex?: "M" | "F" | "X" | null;
}

export interface ProfilePatchResponse {
  ok: boolean;
  job: JobRow;
  enqueued: string[];
  skipped: Array<{ source: string; reason: string }>;
  /** True when first/last/DOB/middle/suffix changed from what was on file —
   *  the modal uses this to prompt "name or DOB changed; re-run scrapes?". */
  identityChanged: boolean;
}

export async function patchJobProfile(
  jobId: string,
  patch: ProfilePatchInput,
): Promise<ProfilePatchResponse> {
  const res = await fetch(`/api/jobs/${jobId}/profile`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(`patchJobProfile failed (${res.status}): ${JSON.stringify(body)}`);
  }
  return (await res.json()) as ProfilePatchResponse;
}

export async function createJobs(people: PersonInput[]): Promise<JobRow[]> {
  const res = await fetch("/api/jobs", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ people }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(`createJobs failed (${res.status}): ${JSON.stringify(body)}`);
  }
  const data = (await res.json()) as { jobs: JobRow[] };
  return data.jobs;
}

// Smarter listing for the /log page — same JobRow set, plus per-job
// classification of the MCCS error (if any) + the most recent failed
// external_lookup per source. Lets the log page label "MCCS error" vs
// "MVC error" and gate the retry button on retryability.
export interface JobErrorClass {
  severity: "info" | "warn" | "alarm";
  label: string;
  explanation: string;
  retry: { likelihood: "good" | "poor" | "cooldown" | "unknown"; advice: string };
}
export interface JobExternalError {
  source: string;            // "doc" | "pgpa" | etc.
  status: "error" | "blocked";
  step: string | null;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
  errorClass: JobErrorClass;
}
export interface JobRowWithErrors extends JobRow {
  mccsErrorClass: JobErrorClass | null;
  externalErrors: JobExternalError[];
  /** Count of external lookups currently queued/running/waiting_captcha
   *  for this job. The Log row uses this so the status badge isn't
   *  misleadingly "completed" while DOC / MVC / etc. are still chugging. */
  externalsInFlight: number;
  /** Source codes (doc, mvc-restoration, …) currently in flight. Backs
   *  the per-job chip ("+3 background scrapes: DOC · MVC restoration · MVC surcharge"). */
  externalsInFlightSources: string[];
  /** Max ISO timestamp across the job AND all external_lookups. Drives
   *  the "last activity" column so the row keeps moving while externals
   *  progress. Null when the job has zero observed activity (rare —
   *  every job has at least a created_at). */
  latestActivityAt: string | null;
}
export async function listJobsForLog(): Promise<JobRowWithErrors[]> {
  const res = await fetch("/api/jobs/_log-detail");
  if (!res.ok) throw new Error(`listJobsForLog failed: ${res.status}`);
  const data = (await res.json()) as { jobs: JobRowWithErrors[] };
  return data.jobs;
}

/** Retry a single external scrape for a person — POSTs to the existing
 *  /api/external/jobs/:jobId endpoint that re-enqueues a fresh attempt
 *  for the named source. Used by the per-source retry buttons on the
 *  /log page. */
export async function retryExternalLookup(jobId: string, source: string): Promise<void> {
  const res = await fetch(`/api/external/jobs/${encodeURIComponent(jobId)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ source }),
  });
  if (!res.ok) throw new Error(`retryExternalLookup failed: ${res.status}`);
}

export async function listJobs(): Promise<JobRow[]> {
  const res = await fetch("/api/jobs");
  if (!res.ok) throw new Error(`listJobs failed: ${res.status}`);
  const data = (await res.json()) as { jobs: JobRow[] };
  return data.jobs;
}

export async function getPersonDetails(jobId: string): Promise<PersonDetails> {
  const res = await fetch(`/api/jobs/${jobId}/records`);
  if (!res.ok) throw new Error(`getPersonDetails failed: ${res.status}`);
  return (await res.json()) as PersonDetails;
}

// — Search-variant telemetry --------------------------------------------
//
// Returned by GET /api/jobs/_search-variants. One row per variant the
// worker tried, joined with the parent job's name + suffix + created_at.
// Powers the /search-variants report page where we can see whether the
// suffix / alias / punctuation permutations are earning their CAPTCHAs.
export interface SearchVariantRow {
  id: number;
  job_id: string;
  variant_index: number;
  label: string;
  first_name: string;
  last_name: string;
  total_rows: number;
  records_added: number;
  duplicates_found: number;
  recorded_at: string;
  // Joined from jobs table
  job_first_name: string;
  job_last_name: string;
  job_suffix: string | null;
  job_created_at: string;
}

export async function listSearchVariants(): Promise<SearchVariantRow[]> {
  const res = await fetch("/api/jobs/_search-variants");
  if (!res.ok) throw new Error(`listSearchVariants failed: ${res.status}`);
  const data = (await res.json()) as { variants: SearchVariantRow[] };
  return data.variants;
}

export async function submitCaptcha(jobId: string, answer: string): Promise<void> {
  const res = await fetch(`/api/jobs/${jobId}/captcha`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ answer }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(`submitCaptcha failed (${res.status}): ${JSON.stringify(body)}`);
  }
}

export async function regenerateCommentary(jobId: string): Promise<void> {
  const res = await fetch(`/api/jobs/${jobId}/regenerate-commentary`, { method: "POST" });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(`regenerate failed (${res.status}): ${JSON.stringify(body)}`);
  }
}

export async function regenerateExpungementStrategy(jobId: string): Promise<void> {
  const res = await fetch(`/api/jobs/${jobId}/regenerate-expungement-strategy`, { method: "POST" });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(`regenerate strategy failed (${res.status}): ${JSON.stringify(body)}`);
  }
}

export async function regenerateBio(jobId: string): Promise<void> {
  const res = await fetch(`/api/jobs/${jobId}/regenerate-bio`, { method: "POST" });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(`bio failed (${res.status}): ${JSON.stringify(body)}`);
  }
}

export interface DigestStats {
  totalPeople: number;
  totalRecords: number;
  totalActive: number;
  totalCompleted: number;
  totalErrors: number;
  totalActiveCases: number;
  peakHavoc: number | null;
  avgHavoc: number | null;
  topHavoc: Array<{ id: string; first_name: string; last_name: string; havoc_score: number }>;
}

export interface Digest {
  greeting: string;
  stats: DigestStats;
  today: string;
}

export async function getDigest(refresh = false): Promise<Digest> {
  const url = refresh ? "/api/digest?refresh=1" : "/api/digest";
  const res = await fetch(url);
  if (!res.ok) throw new Error(`getDigest failed: ${res.status}`);
  return (await res.json()) as Digest;
}

export interface SlideshowSlide {
  id: string;
  jobId: string;
  firstName: string;
  lastName: string;
  offense: string;
  dateText: string;
  category: string;
  imageUrl: string;
}

export async function getCaptureLog(jobId: string): Promise<CaptureLogResponse> {
  const res = await fetch(`/api/jobs/${jobId}/capture-log`);
  if (!res.ok) throw new Error(`getCaptureLog failed: ${res.status}`);
  return (await res.json()) as CaptureLogResponse;
}

export async function getUpcomingDates(): Promise<UpcomingDatesResponse> {
  const res = await fetch("/api/dates/upcoming");
  if (!res.ok) throw new Error(`getUpcomingDates failed: ${res.status}`);
  return (await res.json()) as UpcomingDatesResponse;
}

export async function getSlideshow(): Promise<{ slides: SlideshowSlide[] }> {
  const res = await fetch("/api/slideshow");
  if (!res.ok) throw new Error(`getSlideshow failed: ${res.status}`);
  return (await res.json()) as { slides: SlideshowSlide[] };
}

export async function deleteJob(jobId: string): Promise<void> {
  const res = await fetch(`/api/jobs/${jobId}`, { method: "DELETE" });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(`deleteJob failed (${res.status}): ${JSON.stringify(body)}`);
  }
}

export async function retryJob(jobId: string): Promise<JobRow> {
  const res = await fetch(`/api/jobs/${jobId}/retry`, { method: "POST" });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(`retryJob failed (${res.status}): ${JSON.stringify(body)}`);
  }
  const data = (await res.json()) as { job: JobRow };
  return data.job;
}

// — Notes ----------------------------------------------------------------

export interface NoteRow {
  id: number;
  job_id: string;
  body: string;
  created_at: string;
  updated_at: string | null;
}

export async function listNotes(jobId: string): Promise<NoteRow[]> {
  const res = await fetch(`/api/jobs/${jobId}/notes`);
  if (!res.ok) throw new Error(`listNotes failed: ${res.status}`);
  const data = (await res.json()) as { notes: NoteRow[] };
  return data.notes;
}

export async function createNote(jobId: string, body: string): Promise<NoteRow> {
  const res = await fetch(`/api/jobs/${jobId}/notes`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ body }),
  });
  if (!res.ok) throw new Error(`createNote failed: ${res.status}`);
  const data = (await res.json()) as { note: NoteRow };
  return data.note;
}

export async function updateNote(jobId: string, noteId: number, body: string): Promise<NoteRow> {
  const res = await fetch(`/api/jobs/${jobId}/notes/${noteId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ body }),
  });
  if (!res.ok) throw new Error(`updateNote failed: ${res.status}`);
  const data = (await res.json()) as { note: NoteRow };
  return data.note;
}

export async function deleteNote(jobId: string, noteId: number): Promise<void> {
  const res = await fetch(`/api/jobs/${jobId}/notes/${noteId}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`deleteNote failed: ${res.status}`);
}

// — Ticklers (follow-up reminders) ---------------------------------------

export interface TicklerRow {
  id: number;
  job_id: string;
  due_date: string;          // YYYY-MM-DD
  body: string;
  /** NULL = manually-added by staff. "auto:..." = system-generated
   *  (e.g. license-suspension scanner). UI may treat these visually
   *  differently — auto-tickles get a different chip + can't be edited
   *  in-place (only completed/dismissed). */
  origin: string | null;
  created_by: string | null;
  created_at: string;
  completed_at: string | null;
  completed_by: string | null;
}

export async function listTicklers(jobId: string): Promise<TicklerRow[]> {
  const res = await fetch(`/api/jobs/${jobId}/ticklers`);
  if (!res.ok) throw new Error(`listTicklers failed: ${res.status}`);
  const data = (await res.json()) as { ticklers: TicklerRow[] };
  return data.ticklers;
}

/** Server accepts either "YYYY-MM-DD" or a relative offset like "+14"
 *  (days from today, NJ tz) for dueDate. Both shapes are forwarded as-is. */
export async function createTickler(
  jobId: string,
  input: { dueDate: string; body: string; createdBy?: string },
): Promise<TicklerRow> {
  const res = await fetch(`/api/jobs/${jobId}/ticklers`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`createTickler failed: ${res.status}`);
  const data = (await res.json()) as { tickler: TicklerRow };
  return data.tickler;
}

export async function updateTickler(
  jobId: string,
  ticklerId: number,
  patch: { dueDate?: string; body?: string },
): Promise<TicklerRow> {
  const res = await fetch(`/api/jobs/${jobId}/ticklers/${ticklerId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`updateTickler failed: ${res.status}`);
  const data = (await res.json()) as { tickler: TicklerRow };
  return data.tickler;
}

export async function completeTickler(
  jobId: string,
  ticklerId: number,
  completedBy?: string,
): Promise<TicklerRow> {
  const res = await fetch(`/api/jobs/${jobId}/ticklers/${ticklerId}/complete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(completedBy ? { completedBy } : {}),
  });
  if (!res.ok) throw new Error(`completeTickler failed: ${res.status}`);
  const data = (await res.json()) as { tickler: TicklerRow };
  return data.tickler;
}

export async function reopenTickler(jobId: string, ticklerId: number): Promise<TicklerRow> {
  const res = await fetch(`/api/jobs/${jobId}/ticklers/${ticklerId}/reopen`, { method: "POST" });
  if (!res.ok) throw new Error(`reopenTickler failed: ${res.status}`);
  const data = (await res.json()) as { tickler: TicklerRow };
  return data.tickler;
}

export async function deleteTickler(jobId: string, ticklerId: number): Promise<void> {
  const res = await fetch(`/api/jobs/${jobId}/ticklers/${ticklerId}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`deleteTickler failed: ${res.status}`);
}

// — Today dashboard ------------------------------------------------------

export interface TodayLicenseSuspension {
  jobId: string;
  name: string;
  dob: string;
  caseCount: number;
  earliestCaseDate: string;
}

export interface TodayOpenTickler extends TicklerRow {
  first_name: string;
  last_name: string;
  date_of_birth: string;
}

export interface TodayActiveWarrant {
  jobId: string;
  name: string;
  dob: string;
  court: string;
  caseNumber: string;
  recordId: number;
  // Warrant detail surfaced on the expanded Today panel. All best-effort
  // (MCCS records vary in completeness) — empty strings hide their line.
  offenseCode: string;
  offenseDescription: string;
  warrantNumber: string;
  warrantIssuedDate: string;
  originalOffenseDate: string;
  bail: string;
  status: string;
  courtPhone: string;
  courtAddress: string;
  courtDateTime: string;
  courtRoom: string;
}

export interface TodayWorkerHealth {
  queued: number;
  running: number;
  waitingCaptcha: number;
  errored: number;
  active: number;
  samples: {
    waitingCaptcha: Array<{ id: string; name: string; dob: string }>;
    errored: Array<{ id: string; name: string; dob: string; error?: string }>;
  };
}

export interface TodayFreshIntake {
  jobId: string;
  name: string;
  dob: string;
  completedAt: string;
  havocScore: number | null;
  recordCount: number | null;
  takeawayGenerated: boolean;
  scrapesCompleted: number;
  scrapesTotal: number;
  highPriority: boolean;
  priorityReason: string | null;
}

export interface TodayStuckScrape {
  jobId: string;
  name: string;
  dob: string;
  /** "mccs" for the main job; the source code (e.g. "doc") for an
   *  external lookup. Determines which retry endpoint to call. */
  scrape: string;
  reason: string;
  stuckSince: string;
}

export interface TodayPayload {
  generatedAt: string;
  ticklers: TodayOpenTickler[];
  courtDates: {
    people: Array<{
      jobId: string; name: string; dob: string; havocScore: number | null;
      dates: Array<{
        recordId: number; date: string; statute: string; description: string;
        court: string; status: string; daysOut: number; kind: "court" | "payment" | "other";
      }>;
    }>;
    buckets: Array<{
      key: "today" | "this-week" | "next-week" | "this-month" | "later";
      label: string;
      items: Array<{
        person: { jobId: string; name: string; dob: string; havocScore: number | null };
        date: { recordId: number; date: string; statute: string; description: string;
                court: string; status: string; daysOut: number; kind: "court" | "payment" | "other"; };
      }>;
    }>;
    totalDates: number;
  };
  licenseSuspensions: TodayLicenseSuspension[];
  warrants: TodayActiveWarrant[];
  workerHealth: TodayWorkerHealth;
  freshIntakes: TodayFreshIntake[];
  stuckScrapes: TodayStuckScrape[];
  counts: {
    ticklers: number;
    courtDates: number;
    licenseSuspensions: number;
    warrants: number;
    freshIntakes: number;
    stuckScrapes: number;
  };
}

export async function fetchToday(): Promise<TodayPayload> {
  const res = await fetch(`/api/today`);
  if (!res.ok) throw new Error(`fetchToday failed: ${res.status}`);
  return (await res.json()) as TodayPayload;
}

export interface TodayBrief {
  text: string;
  generatedAt: string;
  cached?: boolean;
  fallback?: boolean;
}

export async function fetchTodayBrief(force = false): Promise<TodayBrief> {
  const url = force ? `/api/today/brief?force=1` : `/api/today/brief`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`fetchTodayBrief failed: ${res.status}`);
  return (await res.json()) as TodayBrief;
}

export interface TodayQuickFindResult {
  jobId: string;
  name: string;
  dob: string;
  status: string;
  havocScore: number | null;
  touched: string | null;
}

// — Idea votes ----------------------------------------------------------

export type IdeaVote = "yes" | "no" | "maybe";
export type IdeaVotesMap = Record<string, IdeaVote>;

export async function fetchIdeaVotes(): Promise<IdeaVotesMap> {
  const res = await fetch("/api/ideas/votes");
  if (!res.ok) return {};
  const data = (await res.json()) as { votes: IdeaVotesMap };
  return data.votes ?? {};
}

/** Pass `null` to clear the vote (toggle off). */
export async function setIdeaVote(
  ideaId: string,
  vote: IdeaVote | null,
  votedBy?: string,
): Promise<void> {
  const res = await fetch(`/api/ideas/${encodeURIComponent(ideaId)}/vote`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ vote, votedBy }),
  });
  if (!res.ok) throw new Error(`setIdeaVote failed: ${res.status}`);
}

export async function quickFindPeople(query: string): Promise<TodayQuickFindResult[]> {
  const q = query.trim();
  if (!q) return [];
  const res = await fetch(`/api/today/search?q=${encodeURIComponent(q)}`);
  if (!res.ok) return [];
  const data = (await res.json()) as { results: TodayQuickFindResult[] };
  return data.results;
}

// — Daily Brief ----------------------------------------------------------

export interface DailyBriefStats {
  prettyDate: string;
  totalPeople: number;
  queueDepth: number;
  errorJobs: number;
  completedOvernight: Array<{ id: string; name: string; havoc: number | null; recordCount: number | null }>;
  topHavocOvernight: { id: string; name: string; havoc: number } | null;
  highestHavocAllTime: { id: string; name: string; havoc: number } | null;
  courtDatesThisWeek: number;
  soonestCourtDate: { name: string; date: string; days: number } | null;
}

export interface DailyBrief {
  date: string;
  title: string;
  body: string;
  stats: DailyBriefStats | null;
  cached: boolean;
}

export async function getDailyBrief(): Promise<DailyBrief> {
  const res = await fetch(`/api/digest/daily-brief`);
  if (!res.ok) throw new Error(`getDailyBrief failed: ${res.status}`);
  return (await res.json()) as DailyBrief;
}

// — Per-record "What were they thinking?" -------------------------------

export interface RecordTakeResponse {
  take: string | null;
  generatedAt: string | null;
  cached?: boolean;
}

export async function getRecordTake(jobId: string, recordId: number): Promise<RecordTakeResponse> {
  const res = await fetch(`/api/jobs/${jobId}/records/${recordId}/take`);
  if (!res.ok) throw new Error(`getRecordTake failed: ${res.status}`);
  return (await res.json()) as RecordTakeResponse;
}

export async function generateRecordTake(jobId: string, recordId: number, force = false): Promise<RecordTakeResponse> {
  const res = await fetch(`/api/jobs/${jobId}/records/${recordId}/take${force ? "?force=1" : ""}`, {
    method: "POST",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(`generateRecordTake failed (${res.status}): ${JSON.stringify(body)}`);
  }
  return (await res.json()) as RecordTakeResponse;
}

// — Story generator -------------------------------------------------------

export interface StoryResult { title: string; subtitle?: string; story: string }

export async function generateStory(jobIds: string[]): Promise<StoryResult> {
  const res = await fetch(`/api/jobs/_generate-story`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ jobIds }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(`generateStory failed (${res.status}): ${JSON.stringify(body)}`);
  }
  return (await res.json()) as StoryResult;
}

export async function continueStory(
  jobIds: string[],
  title: string,
  story: string,
): Promise<{ continuation: string }> {
  const res = await fetch(`/api/jobs/_continue-story`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ jobIds, title, story }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(`continueStory failed (${res.status}): ${JSON.stringify(body)}`);
  }
  return (await res.json()) as { continuation: string };
}

// ─── BATTLE ROYALE ───────────────────────────────────────────────────────

export interface BattleStats {
  strength: number;
  agility: number;
  intelligence: number;
  cunning: number;
  toughness: number;
}

export interface BattleSpecial {
  name: string;
  desc: string;
  glyph: string;
}

export interface Combatant {
  id: string;
  name: string;
  alias: string;
  archetype: string;
  city: string;
  era: string;
  havocScore: number;
  recordCount: number;
  hasWarrant: boolean;
  charges: string[];
  topOffenses: string[];
  stats: BattleStats;
  specials: BattleSpecial[];
  signature: string;
  loreQuote: string;
  portrait: string;
  accent: string;
}

export interface BattleBeat {
  side: "player" | "opponent";
  attacker: string;
  defender: string;
  stat: keyof BattleStats;
  fx: "punch" | "shot" | "dash" | "shock" | "grapple" | "finisher" | "counter" | "misc";
  /** Long narration shown in the bottom HUD (1-2 sentences). */
  text: string;
  /** PUNCHY 3-7 word ALL-CAPS slam label rendered as giant comic-book
   *  text in the center of the screen. Server may or may not provide this
   *  (older procedural fallbacks omit it); the client treats it as
   *  optional and synthesizes one from the text if missing. */
  punch?: string;
  damage: number;
}

export interface BattleScript {
  introNarration: string;
  playerTaunt: string;
  opponentTaunt: string;
  beats: BattleBeat[];
  victor: "player" | "opponent";
  outroNarration: string;
}

export async function fetchBattleRoster(): Promise<Combatant[]> {
  const res = await fetch("/api/battle/roster");
  if (!res.ok) throw new Error(`fetchBattleRoster failed (${res.status})`);
  const json = await res.json() as { combatants: Combatant[] };
  return json.combatants;
}

export async function fetchBattleScript(
  playerIds: string[],
  opponentIds: string[],
): Promise<BattleScript> {
  const res = await fetch("/api/battle/script", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ playerIds, opponentIds }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(`fetchBattleScript failed (${res.status}): ${JSON.stringify(body)}`);
  }
  return (await res.json()) as BattleScript;
}
